/**
 * describe_page tool — Task C3
 *
 * Live grounding for the current tenant: returns which KPIs and dim values are
 * actually present on a page right now.  The model calls this before suggesting
 * filters or group-bys so it never drifts on real data.
 *
 * Design decisions:
 * - get_kpis_for_page and get_charts (used to resolve the controls fact) are
 *   fired in parallel via Promise.all; get_controls then runs against the
 *   resolved fact.
 * - The get_controls fact is resolved the way the dashboard does
 *   (useReviewPageState): fetch the page's default-KPI chart_registry rows and
 *   use the first row's default_params.fact_table, falling back to the catalog's
 *   static factTable when the registry carries no string fact. get_controls'
 *   kpi_metadata is fact-independent, but dims_view is fact-scoped — so the fact
 *   determines which dim values come back (e.g. ReviewKpis resolves to
 *   kpi_overall, not the static media_national).
 * - Dim values are derived from dims_view[].dims, NOT a top-level dim_values
 *   field (which does not exist — per the getControls memory note).
 * - Null-dims rows are skipped; values per dim are de-duplicated and sorted.
 * - All errors surface as isError:true; nothing escapes run().
 */

import type { ApiClient } from '../../api/apiClient'
import { getKpisForPage } from '../../api/endpoints/getKpisForPage'
import { getControls } from '../../api/endpoints/getControls'
import { getCharts } from '../../api/endpoints/getCharts'
import { PAGE_CATALOG } from '../grounding/pageCatalog'
import type { PageInfo } from '../grounding/pageCatalog'
import type { AdvisorTool, ToolResult } from '../runtime/types'

// Dynamic access helper: PAGE_CATALOG uses `satisfies` (retains literal keys)
// but callers receiving pageId at runtime must cast to avoid index-type errors.
const CATALOG = PAGE_CATALOG as Record<string, PageInfo | undefined>

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function makeDescribePageTool(ctx: { apiClient: ApiClient }): AdvisorTool {
  const { apiClient } = ctx

  return {
    name: 'describe_page',

    description:
      'The drill-down companion to the Dataset Overview: returns the COMPLETE live dim-value ' +
      'lists and available KPIs for one page (the overview carries only counts + samples). ' +
      'Call before filtering or grouping on a dim value you have not seen verbatim. ' +
      'Never fabricate dim values — look them up here.',

    input_schema: {
      type: 'object',
      properties: {
        pageId: {
          type: 'string',
          description:
            'Identifier of the analytics page. One of: ReviewKpis, MarketingInvests, ' +
            'CampaignPerformance, Contribution, Dynamics, MultiDeepDive.',
        },
      },
      required: ['pageId'],
    },

    async run(input: unknown, signal?: AbortSignal): Promise<ToolResult> {
      try {
        // ------------------------------------------------------------------
        // 1. Narrow input
        // ------------------------------------------------------------------
        if (typeof input !== 'object' || input === null) {
          return { content: 'describe_page: input must be a JSON object', isError: true }
        }
        const inp = input as Record<string, unknown>
        const pageId = typeof inp['pageId'] === 'string' ? inp['pageId'] : undefined
        if (!pageId) {
          return {
            content: 'describe_page: "pageId" is required and must be a string',
            isError: true,
          }
        }

        // ------------------------------------------------------------------
        // 2. Resolve page info via PAGE_CATALOG
        // ------------------------------------------------------------------
        const page = CATALOG[pageId]
        if (!page) {
          return {
            content: `describe_page: Unknown page: ${pageId}`,
            isError: true,
          }
        }

        // ------------------------------------------------------------------
        // 3. Resolve the controls fact from the live registry, then fetch
        //    KPIs + controls. The registry-resolved fact wins; the catalog's
        //    static factTable is only a fallback for when the default-KPI rows
        //    carry no string fact (mirrors useReviewPageState's activeFactTable —
        //    the same fallback the dashboard uses for ratio-default pages).
        // ------------------------------------------------------------------
        const [availableKpis, defaultKpiCharts] = await Promise.all([
          getKpisForPage(apiClient, pageId, signal),
          getCharts(apiClient, pageId, page.defaultKpi, signal),
        ])
        const registryFact = defaultKpiCharts[0]?.default_params?.['fact_table']
        const controlsFact = typeof registryFact === 'string' ? registryFact : page.factTable
        const controls = await getControls(apiClient, controlsFact, signal)

        // ------------------------------------------------------------------
        // 4. Derive dims: Record<string, string[]> from dims_view
        //    - skip rows where dims is null
        //    - de-duplicate values per dim name using a Set
        //    - sort each dim's values for deterministic output
        // ------------------------------------------------------------------
        const dimSets: Record<string, Set<string>> = {}
        for (const row of controls.dims_view) {
          if (row.dims == null) continue
          for (const [name, value] of Object.entries(row.dims)) {
            if (!Object.prototype.hasOwnProperty.call(dimSets, name)) {
              dimSets[name] = new Set()
            }
            dimSets[name]!.add(value)
          }
        }
        const dims: Record<string, string[]> = {}
        for (const [name, set] of Object.entries(dimSets)) {
          dims[name] = Array.from(set).sort()
        }

        // ------------------------------------------------------------------
        // 5. Return compact JSON result
        // ------------------------------------------------------------------
        return { content: JSON.stringify({ pageId, availableKpis, dims }) }
      } catch (err) {
        // ------------------------------------------------------------------
        // 6. Nothing escapes run()
        // ------------------------------------------------------------------
        return {
          content: err instanceof Error ? err.message : String(err),
          isError: true,
        }
      }
    },
  }
}
